import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PersonalisationLoader } from '../PersonalisationLoader/index';

import styles from './star-map-generator.module.css';

/**
 * StarMapGenerator component generates and displays a star map based on provided data.
 * 
 * @param {Object} props - The component props
 * @param {Object} props.starMapData - The star map data object with datetime, time, location, locationPlace
 * @param {boolean} [props.autoGenerate=true] - Whether to automatically generate when data is available
 * @param {Function} [props.onGenerate] - Optional callback when generation starts
 * @param {Function} [props.onSuccess] - Optional callback when generation succeeds
 * @param {Function} [props.onError] - Optional callback when generation fails
 * @returns {import('react').JSX.Element} The rendered component
 */
export const StarMapGenerator = ({
  starMapData,
  autoGenerate = true,
  onGenerate,
  onSuccess,
  onError
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [starMapImageUrl, setStarMapImageUrl] = useState(/** @type {string | null} */(null));
  const [error, setError] = useState(/** @type {string | null} */(null));

  /**
   * Gets timezone identifier. For now, uses user's browser timezone.
   * In production, consider using a timezone lookup API based on coordinates.
   * @param {number} lat - Latitude (not currently used, but available for future enhancement)
   * @param {number} lng - Longitude (not currently used, but available for future enhancement)
   * @returns {string} Timezone identifier (e.g., "America/New_York")
   */
  const getTimezoneFromLocation = (lat, lng) => {
    // For now, use the user's browser timezone.
    // In production, you might want to use a service like:
    // - Google Time Zone API
    // - TimeZoneDB API
    // - GeoNames API
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log(`Using timezone: ${timeZone} for coordinates (${lat}, ${lng})`);
      return timeZone || 'UTC';
    } catch (error) {
      console.warn('Could not determine timezone, using UTC:', error);
      return 'UTC';
    }
  };

  /**
   * Formats the datetime string to YYYY-MM-DD format for API.
   * @param {string} datetime - The datetime string (could be ISO format or date input format)
   * @returns {string} Formatted date string (YYYY-MM-DD)
   */
  const formatDateForAPI = (datetime) => {
    if (!datetime) return '';

    try {
      // If it's already in YYYY-MM-DD format, return as is.
      if (/^\d{4}-\d{2}-\d{2}$/.test(datetime)) {
        return datetime;
      }

      // Parse the date and format it.
      const date = new Date(datetime);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date format:', datetime);
        return '';
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  /**
   * Formats the datetime string to display format (dd MMMM YYYY).
   * @param {string} datetime - The datetime string (could be ISO format or date input format)
   * @returns {string} Formatted date string (e.g., "15 January 2024")
   */
  const formatDateForDisplay = (datetime) => {
    if (!datetime) return 'Not set';

    try {
      const date = new Date(datetime);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date format:', datetime);
        return 'Not set';
      }

      // Format as dd MMMM YYYY (e.g., "15 January 2024").
      const day = date.getDate();
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();

      return `${day} ${month} ${year}`;
    } catch (error) {
      console.error('Error formatting date for display:', error);
      return 'Not set';
    }
  };

  /**
   * Generates a star map by calling the API.
   */
  const generateStarMap = useCallback(async () => {
    setIsGenerating(true);
    setError(null);
    setStarMapImageUrl(null);

    if (onGenerate) {
      onGenerate();
    }

    try {
      // Default location coordinates for Leeds, UK.
      const defaultLocation = { lat: 53.8008, lng: -1.5491 };
      // Use location from starMapData if available, otherwise use default (Leeds).
      const lat = starMapData?.locationPlace?.location?.lat || defaultLocation.lat;
      const lng = starMapData?.locationPlace?.location?.lng || defaultLocation.lng;

      if (!starMapData?.datetime) {
        throw new Error('Date is required');
      }

      const date = formatDateForAPI(starMapData.datetime);

      // Default to midnight (00:00) if time not provided.
      let time = starMapData.time;
      if (!time) {
        time = '00:00';
      }

      const timezone = getTimezoneFromLocation(lat, lng);

      if (!date) {
        throw new Error('Invalid date format');
      }

      // Prepare request body.
      const requestBody = {
        latitude: lat,
        longitude: lng,
        date: date,
        time: time,
        timezone: timezone,
        invertMonochrome: false
      };

      console.log('Generating star map with data:', requestBody);
      // Get API URL from environment variable or window variable, or use default.
      // Supports both relative URLs (same domain) and absolute URLs (e.g., localhost:3000).
      const apiUrl = import.meta.env.VITE_STARMAP_API_URL;

      // If it's a relative URL and we have a base URL configured, use it.
      const fullApiUrl = apiUrl.startsWith('http')
        ? apiUrl
        : apiUrl.startsWith('/') && window.location.origin
          ? `${window.location.origin}${apiUrl}`
          : apiUrl;

      // Make POST request to generate star map.
      const response = await fetch(fullApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate star map: ${response.status} ${errorText}`);
      }
      // Check if response is an image (based on Content-Type header).
      const contentType = response.headers.get('Content-Type');

      if (contentType && contentType.includes('image/')) {
        // If the API returns an image directly (binary or base64), convert it to a data URL.
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result && typeof reader.result === 'string') {
            setStarMapImageUrl(reader.result);
            if (onSuccess) {
              onSuccess(reader.result);
            }
          }
        };
        reader.readAsDataURL(blob);
      } else {
        // If the API returns JSON with image data.
        const data = await response.json();

        let imageUrl = null;
        let invertedImageUrl = null;

        // Check for new API response structure with starMaps.display and starMaps.inverted.
        if (data.starMaps?.display) {
          // Convert base64 string to data URL if needed for display version.
          const displayImage = data.starMaps.display;
          imageUrl = displayImage.startsWith('data:') ? displayImage : `data:image/png;base64,${displayImage}`;

          // Also capture the inverted version for saving.
          if (data.starMaps?.inverted) {
            const invertedImage = data.starMaps.inverted;
            invertedImageUrl = invertedImage.startsWith('data:') ? invertedImage : `data:image/png;base64,${invertedImage}`;
          }
        } else if (data.imageUrl) {
          imageUrl = data.imageUrl;
        } else if (data.image) {
          // If it's base64, convert it to a data URL.
          console.log('Data image:', data.image);
          imageUrl = data.image.startsWith('data:') ? data.image : `data:image/png;base64,${data.image}`;
        } else {
          throw new Error('No image data returned from API');
        }

        setStarMapImageUrl(imageUrl);
        if (onSuccess) {
          // Pass the inverted version (which is what needs to be saved) to the success callback.
          onSuccess(invertedImageUrl || imageUrl);
        }
      }
    } catch (error) {
      console.error('Error generating star map:', error);
      const errorMessage = error.message || 'Failed to generate star map';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsGenerating(false);
    }
  }, [starMapData, onGenerate, onSuccess, onError]);

  // Check if we have all required data to generate a star map.
  const canGenerateStarMap =
    starMapData?.locationPlace?.location?.lat &&
    starMapData?.locationPlace?.location?.lng &&
    starMapData?.datetime;

  // Track if we've already generated the star map to avoid duplicate calls.
  const hasGeneratedRef = useRef(false);
  const previousDataRef = useRef(/** @type {string | null} */(null));

  // Automatically generate star map when data is available.
  useEffect(() => {
    if (!autoGenerate) {
      return;
    }

    // Check if we have valid data and haven't already generated.
    if (!canGenerateStarMap) {
      hasGeneratedRef.current = false;
      return;
    }

    // Check if the data has changed (to regenerate if user updates date/location/time).
    const currentTime = starMapData?.time || '00:00';
    const currentDataKey = `${starMapData?.datetime}-${currentTime}-${starMapData?.locationPlace?.location?.lat}-${starMapData?.locationPlace?.location?.lng}`;

    if (hasGeneratedRef.current && previousDataRef.current === currentDataKey) {
      // Already generated for this data, skip.
      return;
    }

    // Generate the star map automatically.
    previousDataRef.current = currentDataKey;
    hasGeneratedRef.current = true;
    generateStarMap();
  }, [autoGenerate, canGenerateStarMap, starMapData?.datetime, starMapData?.time, starMapData?.locationPlace?.location?.lat, starMapData?.locationPlace?.location?.lng, generateStarMap]);

  return (
    <div className={styles['star-map-generator']}>
      {/* Star Map Generation Status */}
      {canGenerateStarMap && (
        <div className={styles['star-map-generator__status']}>
          {isGenerating && (
            <>
              <PersonalisationLoader isVisible={true} />
              <p className="body-sm">Generating Star map...</p>
            </>
          )}
          {error && (
            <p className={styles['star-map-generator__error']}>Error: {error}</p>
          )}
          {starMapImageUrl && (
            <div className={styles['star-map-generator__image']}>
              <img
                src={starMapImageUrl}
                className={styles['star-map-generator__image-element']}
                alt="Generated Star Map"
                style={{ maxWidth: '100%' }}
              />
            </div>
          )}
        </div>
      )}

      {!canGenerateStarMap && (
        <p className="body-sm font-regular">Please provide date and location to generate a star map.</p>
      )}
      {canGenerateStarMap && (
        <div className={styles['star-map-generator__data']}>
          <p>{formatDateForDisplay(starMapData.datetime)} at {starMapData.time || 'Midnight'}</p>
          {starMapData.locationPlace && (
            <>
              {starMapData.locationPlace.formattedAddress && (
                <p className={"body-sm font-regular"}>{starMapData.locationPlace.formattedAddress}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

